import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { MOVEMENT_TYPES, formatQty, type MovementType } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { Alert, Badge, Button, Card, PageHeader, Spinner, cx } from '../../ui/index.tsx';
import { useCatalog } from '../products/data.ts';
import { formatStock, useStockMap, useStockRows } from './data.ts';

interface MovementView {
  id: string;
  type: MovementType;
  qty_milli: number;
  reason: string | null;
  occurred_at: string;
  created_by: string | null;
  device_id: string | null;
  pending: boolean;
}

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });
const LIMIT = 100;

/** Historial de movimientos de un producto, con el saldo después de cada uno. */
export function MovementsPage() {
  const { variantId } = useParams();
  const { business } = useSession();
  const online = useOnline();
  const catalog = useCatalog();
  const stock = useStockMap();
  const rows = useStockRows(catalog, stock);
  const row = rows.find((r) => r.variant.id === variantId);
  const [remote, setRemote] = useState<MovementView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const local = useLiveQuery(() => db.stock_movements.where('variant_id').equals(variantId ?? '').toArray(), [variantId]);
  const people = useLiveQuery(() => db.memberships.where('business_id').equals(business.id).toArray(), [business.id]);
  const devices = useLiveQuery(() => db.devices.where('business_id').equals(business.id).toArray(), [business.id]);

  useEffect(() => {
    if (!online || !variantId) return;
    let cancelled = false;
    void supabase
      .from('stock_movements')
      .select('id, type, qty_milli, reason, occurred_at, created_by, device_id')
      .eq('variant_id', variantId)
      .order('occurred_at', { ascending: false })
      .limit(LIMIT)
      .then(({ data, error: e }) => {
        if (cancelled) return;
        if (e) setError('No se pudo traer el historial del servidor.');
        else setRemote((data ?? []).map((m) => ({ ...m, pending: false }) as MovementView));
      });
    return () => {
      cancelled = true;
    };
  }, [online, variantId, local?.length]);

  const movements = useMemo(() => {
    const byId = new Map<string, MovementView>((remote ?? []).map((m) => [m.id, m]));
    for (const m of local ?? []) {
      if (!byId.has(m.id)) {
        byId.set(m.id, { ...m, created_by: m.created_by ?? null, pending: !m.server_xid });
      }
    }
    const list = [...byId.values()].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
    // Saldo después de cada movimiento, desde el stock actual hacia atrás.
    let balance = row?.qty ?? 0;
    return list.map((m) => {
      const after = balance;
      balance -= m.qty_milli;
      return { ...m, after };
    });
  }, [remote, local, row?.qty]);

  if (!catalog || !stock) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }
  if (!row) {
    return (
      <Card className="mx-auto max-w-lg text-center">
        <p className="font-medium">No encontramos ese producto.</p>
        <Link to="/stock" className="mt-3 inline-block text-sm font-medium text-brand">
          Volver al stock
        </Link>
      </Card>
    );
  }

  const unit = row.item.product.unit_type;
  const person = (id: string | null) => (id ? (people?.find((p) => p.user_id === id)?.display_name ?? '—') : '—');
  const device = (id: string | null) => (id ? (devices?.find((d) => d.id === id)?.name ?? null) : null);
  // Sin conexión faltan movimientos de otros dispositivos: el saldo no sería confiable.
  const showBalance = online && remote !== null;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <PageHeader
        title={row.label}
        description={`Stock actual: ${formatStock(row)}`}
        actions={
          <Link to="/stock">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />
      {!online && <Alert tone="warning">Sin conexión: se ven solo los movimientos hechos en este dispositivo que todavía no se borraron.</Alert>}
      {error && <Alert tone="error">{error}</Alert>}
      {online && remote === null && !error && (
        <div className="flex justify-center py-6 text-slate-500">
          <Spinner />
        </div>
      )}
      <Card flush className="divide-y divide-slate-100">
        {movements.length === 0 && remote !== null && <p className="p-6 text-center text-sm text-slate-500">Todavía no tiene movimientos.</p>}
        {movements.map((m) => (
          <div key={m.id} className="flex items-center gap-3 px-4 py-3 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {MOVEMENT_TYPES[m.type]?.label ?? m.type}
                {m.pending && (
                  <span className="ml-2">
                    <Badge tone="amber">Sin enviar</Badge>
                  </span>
                )}
              </p>
              <p className="truncate text-slate-500">
                {[dateFormat.format(new Date(m.occurred_at)), person(m.created_by), device(m.device_id), m.reason].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <p className={cx('font-semibold', m.qty_milli < 0 ? 'text-red-700' : 'text-emerald-700')}>
                {m.qty_milli > 0 ? '+' : ''}
                {formatQty(m.qty_milli, unit)}
              </p>
              {showBalance && (
                <p className="text-xs text-slate-500">queda {formatQty(m.after, unit)}</p>
              )}
            </div>
          </div>
        ))}
      </Card>
      {remote !== null && remote.length === LIMIT && <p className="text-center text-sm text-slate-500">Se muestran los últimos {LIMIT} movimientos.</p>}
    </div>
  );
}
